import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faRobot, faComments, faShieldAlt, faLightbulb, faEnvelope, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const LoginContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(-45deg, var(--primary-600), var(--primary-700), var(--primary-800), var(--primary-600));
  background-size: 400% 400%;
  animation: ${gradientAnimation} 15s ease infinite;
  padding: var(--space-4);
  box-sizing: border-box;
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: var(--space-8);
  border-radius: var(--radius-2xl);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  max-height: calc(100vh - 2rem);
`;

const Logo = styled.div`
  font-size: var(--text-4xl);
  color: var(--primary-600);
  margin-bottom: var(--space-4);
  text-align: center;
`;

const Title = styled.h1`
  color: var(--text-color);
  font-size: var(--text-2xl);
  font-weight: 600;
  margin-bottom: var(--space-2);
  text-align: center;
  font-family: var(--font-heading-bold);
`;

const Subtitle = styled.p`
  color: var(--text-color);
  font-size: var(--text-base);
  line-height: 1.5;
  margin-bottom: var(--space-6);
  text-align: center;
  opacity: 0.8;
  font-family: var(--font-family);
  font-weight: 400;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background-color: var(--surface-color);
  color: var(--text-color);
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--space-3) var(--space-5);
  font-size: var(--text-base);
  color: white;
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  font-family: var(--font-family);
  font-weight: 400;
  box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-800) 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(90, 99, 230, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  &:disabled {
    background: var(--neutral-400);
    cursor: not-allowed;
    transform: none;
  }

  svg {
    margin-right: 0.5rem;
    font-size: 1.1rem;
  }
`;

const GoogleButton = styled(Button)`
  background: linear-gradient(135deg, #4285F4 0%, #3367D6 100%);
  color: white;
  border: none;

  &:hover {
    background: linear-gradient(135deg, #3367D6 0%, #2a56c6 100%);
  }
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: var(--space-4);
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
  margin-top: var(--space-6);
  width: 100%;
`;

const Feature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  svg {
    font-size: var(--text-xl);
    color: var(--primary-600);
    margin-bottom: var(--space-2);
  }

  span {
    font-size: var(--text-sm);
    font-family: var(--font-heading-regular);
    color: var(--text-secondary);
    font-weight: 300;
  }
`;

const SecurityNote = styled.p`
  font-size: var(--text-xs);
  opacity: 0.7;
  margin-top: var(--space-4);
  text-align: center;
  font-family: var(--font-family);
  font-weight: 400;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  color: var(--error-600);
  background-color: var(--error-50);
  border: 1px solid var(--error-200);
  border-radius: var(--radius-lg);
  padding: var(--space-3);
  margin-bottom: var(--space-4);
  font-size: var(--text-sm);
  font-family: var(--font-family);
  font-weight: 400;
  width: 100%;
  display: flex;
  align-items: center;
  font-weight: 500;

  svg {
    margin-left: var(--space-2);
    flex-shrink: 0;
  }
`;

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('Current colors:', {
      primaryColor: getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim(),
      secondaryAccent: getComputedStyle(document.documentElement).getPropertyValue('--secondary-accent').trim(),
      primaryColorDark: getComputedStyle(document.documentElement).getPropertyValue('--primary-color-dark').trim(),
      accentColor: getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim(),
      backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--background-color').trim(),
    });
  }, []);

  const signInWithGoogle = () => {
    signInWithPopup(auth, googleProvider)
      .then((result) => {
        console.log("User signed in successfully");
      }).catch((error) => {
        console.error("Error signing in:", error.message);
        setError(error.message);
      });
  }

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    console.log("Attempting email auth. Sign up:", isSignUp);
    try {
      if (isSignUp) {
        console.log("Attempting to create new user");
        await createUserWithEmailAndPassword(auth, email, password);
        console.log("User signed up successfully");
      } else {
        console.log("Attempting to sign in existing user");
        await signInWithEmailAndPassword(auth, email, password);
        console.log("User signed in successfully");
      }
    } catch (error) {
      console.error("Detailed error:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      setError(getErrorMessage(error.code));
    }
  }

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please try signing in instead.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Invalid email or password.';
      case 'auth/operation-not-allowed':
        return 'Email/password sign-in is not enabled. Please contact support.';
      default:
        return `An error occurred: ${errorCode}`;
    }
  }

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <FontAwesomeIcon icon={faRobot} />
        </Logo>
        <Title style={{
          fontFamily: 'var(--font-heading-bold)',
          marginBottom: 'var(--space-2)',
          textAlign: 'center',
          color: 'var(--text-color)',
          fontSize: 'var(--text-2xl)',
          fontWeight: '800'
        }}>Welcome to SuperChat</Title>
        <Subtitle style={{
          fontFamily: 'var(--font-heading-regular)',
          marginBottom: 'var(--space-6)',
          textAlign: 'center',
          opacity: '0.8',
          color: 'var(--text-color)',
          fontSize: 'var(--text-base)',
          lineHeight: '1.5',
          fontWeight: '300'
        }}>Experience the future of AI-powered communication</Subtitle>
        
        {error && (
          <ErrorMessage>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            {error}
          </ErrorMessage>
        )}
        
        <Form onSubmit={handleEmailAuth}>
          <Input 
            type="email" 
            placeholder="Email" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input 
            type="password" 
            placeholder="Password" 
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <ButtonGroup>
            <Button type="submit">
              <FontAwesomeIcon icon={faEnvelope} />
              {isSignUp ? 'Sign Up' : 'Sign In'} with Email
            </Button>
            <GoogleButton onClick={signInWithGoogle}>
              <FontAwesomeIcon icon={faGoogle} />
              Sign in with Google
            </GoogleButton>
          </ButtonGroup>
        </Form>
        
        <p style={{
          fontSize: 'var(--text-sm)',
          textAlign: 'center',
          margin: 'var(--space-4) 0',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-heading-regular)',
          fontWeight: '400'
        }}>
          {isSignUp
            ? "Already have an account? "
            : "Don't have an account? "}
          <a href="#" onClick={(e) => { e.preventDefault(); setIsSignUp(!isSignUp); setError(''); }} style={{
            color: 'var(--primary-600)',
            textDecoration: 'none',
            fontWeight: '600',
            transition: 'color 0.3s ease',
            fontFamily: 'var(--font-heading-regular)'
          }}>
            {isSignUp ? "Sign In" : "Sign Up"}
          </a>
        </p>

        <Features>
          <Feature>
            <FontAwesomeIcon icon={faRobot} />
            <span>Advanced AI</span>
          </Feature>
          <Feature>
            <FontAwesomeIcon icon={faComments} />
            <span>Smart Conversations</span>
          </Feature>
          <Feature>
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>Secure & Private</span>
          </Feature>
          <Feature>
            <FontAwesomeIcon icon={faLightbulb} />
            <span>Innovative Insights</span>
          </Feature>
        </Features>
        <SecurityNote style={{
          fontFamily: 'var(--font-heading-regular)',
          fontSize: 'var(--text-xs)',
          opacity: '0.7',
          marginTop: 'var(--space-4)',
          textAlign: 'center',
          color: 'var(--text-secondary)',
          fontWeight: '300'
        }}>
          <FontAwesomeIcon icon={faShieldAlt} style={{ marginLeft: 'var(--space-2)' }} />
          Your privacy and security are our top priorities
        </SecurityNote>
      </LoginCard>
    </LoginContainer>
  );
}

export default Login;